<script lang="ts" setup>
import { onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import type { NotificationCategory } from '@roadfootage/api-client'
import { useNotificationPreferencesStore } from '../../stores/notification-preferences'

const $q = useQuasar()
const store = useNotificationPreferencesStore()
const { preferences, loading, loadFailed } = storeToRefs(store)

async function onToggle(category: NotificationCategory, enabled: boolean) {
  const result = await store.setEmailEnabled(category, enabled)
  if (result.ok) return
  $q.notify({ color: 'negative', textColor: 'white', icon: 'mdi-alert', message: result.message })
}

onMounted(() => store.load())
</script>

<template>
  <q-card flat bordered id="notifications" class="settings-card" data-testid="notifications-card">
    <q-card-section>
      <div class="text-h6">Notifications</div>
      <div class="text-caption text-grey-7">Email</div>
    </q-card-section>

    <q-card-section v-if="loading && preferences.length === 0" class="text-center">
      <q-spinner color="primary" size="24px" />
    </q-card-section>
    <q-card-section v-else-if="loadFailed" class="column items-start q-gutter-sm">
      <div>We could not load your notification settings.</div>
      <q-btn outline label="Try again" @click="store.load()" />
    </q-card-section>

    <q-list v-else separator>
      <q-item
        v-for="pref in preferences"
        :key="pref.category"
        :data-testid="`notif-row-${pref.category}`"
      >
        <q-item-section>
          <q-item-label>{{ pref.displayName }}</q-item-label>
          <q-item-label caption>
            {{ pref.isTransactional ? 'Always sent' : pref.description }}
          </q-item-label>
        </q-item-section>
        <q-item-section side>
          <q-toggle
            :model-value="pref.emailEnabled"
            :disable="pref.isTransactional"
            :aria-label="`${pref.displayName} emails`"
            :data-testid="`notif-toggle-${pref.category}`"
            @update:model-value="(value: boolean) => onToggle(pref.category!, value)"
          />
        </q-item-section>
      </q-item>
    </q-list>
  </q-card>
</template>
